import React, { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Tag, Alert, Radio, Flex } from "antd";
import BtnAsLink from "../../../Component/BtnAsLink";
import { useLocalizedText } from "../../../utils/hooks/useLocalizedText";
import { TableC } from "../../../CommonElements/TableC/TableC";
import { Stack } from "react-bootstrap";
import { Cancel01Icon, HajiIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { SelectCountry } from "../../../Component/Form/SelectCountry/SelectCountry";
import { Label, Spinner } from "reactstrap";
import { useDebouncedSearch } from "../../../utils/hooks/useDebouncedSearch";
import { AntInput } from "../../../CommonElements/AntInput";
import { useParams } from "react-router-dom";
import { LabelValue } from "@/Component/Value";
import { PilgrimGroupNameMappingInfiniteSelect } from "@/Component/InfiniteSearchSelect/PilgrimGroupNameMappingInfiniteSelect";
import { usePilgrimGroupNames } from "@/services/basicData/basicData";
import { Can } from "@/CommonElements/PermissionGate";
import { permissions } from "@/utils/constants";
/**
 * AccommodationTable Component
 * Displays pilgrims with checkboxes for selection using TableC
 * Shows accommodation status and allows bulk accommodation operations
 */
export const AccommodationTable = ({
  data = [],
  loading = false,
  searchValue = "",
  onSearchChange,
  pagination,
  selectionHook,
  onAccommodate,
  onCancelAccommodate,
  onPageSizeChange,
  onFilterChange,
  filter = {},
  mission_id,
  company_id,
  isAccommodating = false,
  mutationsVariables = null,
}) => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const { type } = useParams();
  const ACCOMMODATION_FILTER_ALL = "all";
  const [accommodationStatusFilter, setAccommodationStatusFilter] = useState(
    filter.is_accommodated === 0 || filter.is_accommodated === 1
      ? filter.is_accommodated
      : ACCOMMODATION_FILTER_ALL
  );

  // Add debounced number filter for manifest_id
  const {
    searchValue: numberFilterValue,
    debouncedValue: debouncedNumberFilter,
    setSearchValue: setNumberFilterValue,
  } = useDebouncedSearch("", 750);
  const {
    searchValue: tafweejIdFilterValue,
    debouncedValue: debouncedTafweejIdFilter,
    setSearchValue: setTafweejIdFilterValue,
  } = useDebouncedSearch("", 750);

  // Sync accommodationStatusFilter with filter prop (0, 1, or "all")
  useEffect(() => {
    const v = filter.is_accommodated;
    setAccommodationStatusFilter(
      v === 0 || v === 1 ? v : ACCOMMODATION_FILTER_ALL
    );
  }, [filter.is_accommodated]);

  // Raw group names options depend on organizer + affiliated organizer
  const missionIdForGroupNames = mission_id ?? filter?.mission_id;
  const companyIdForGroupNames = company_id ?? filter?.company_id;

  const prevMissionIdForMappedGroupRef = useRef();

  useEffect(() => {
    if (!onFilterChange) return;

    const prev = prevMissionIdForMappedGroupRef.current;
    const nextMission = missionIdForGroupNames;

    if (
      prev !== undefined &&
      String(prev ?? "") !== String(nextMission ?? "")
    ) {
      const mappingId = filter?.group_name_mapping_id;
      if (mappingId != null && mappingId !== "") {
        const next = { ...filter };
        delete next.group_name_mapping_id;
        onFilterChange(next);
      }
    }

    prevMissionIdForMappedGroupRef.current = nextMission;
  }, [missionIdForGroupNames, filter, onFilterChange]);

  const {
    data: pilgrimGroupNamesRes,
    isLoading: isLoadingPilgrimGroupNames,
  } = usePilgrimGroupNames({
    company_id: companyIdForGroupNames,
    mission_id: missionIdForGroupNames,
  });
  const rawGroupNamesOptions = useMemo(() => {
    const list = pilgrimGroupNamesRes?.data?.data ?? [];
    return (Array.isArray(list) ? list : []).map((m) => ({
      value: m.raw_group_name,
      label:
        <LabelValue.Value
          value={getLocalizedText(m.raw_group_name_ar, m.raw_group_name_en) ?? m.raw_group_name ?? ""}
        />,
    }));
  }, [pilgrimGroupNamesRes, getLocalizedText]);

  // Handle number input change - only allow numbers
  const handleNumberFilterChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === "" || /^\d+$/.test(value)) {
      setNumberFilterValue(value);
    }
  };

  const handleTafweejIdFilterChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === "" || /^\d+$/.test(value)) {
      setTafweejIdFilterValue(value);
    }
  };

  // Update filter when debounced value changes
  useEffect(() => {
    onFilterChange({
      ...filter,
      manifest_id: debouncedNumberFilter,
      tafweej_id: debouncedTafweejIdFilter,
    });
  }, [debouncedNumberFilter, debouncedTafweejIdFilter]);

  const {
    selectedCount,
    isLimitReached,
    isPilgrimSelected,
    handleSelectOne,
    handleSelectAll,
    isPageFullySelected,
    isPagePartiallySelected,
    canSelectPilgrim,
    MAX_SELECTION,
  } = selectionHook;

  // Check if all page is selected
  const allPageSelected = useMemo(() => {
    return isPageFullySelected(data);
  }, [data, isPageFullySelected]);

  // Check if page is partially selected
  const pagePartiallySelected = useMemo(() => {
    return isPagePartiallySelected(data);
  }, [data, isPagePartiallySelected]);

  // Get nationality name from basic data
  const getNationalityName = (nationalityId) => {
    return <SelectCountry.Display value={nationalityId} />;
  };

  // Handle header checkbox change
  const handleHeaderCheckboxChange = (e) => {
    handleSelectAll(e.target.checked, data);
  };

  // Handle row checkbox change
  const handleRowCheckboxChange = (pilgrim, checked) => {
    handleSelectOne(pilgrim, checked);
  };

  // Render accommodation status
  const renderStatus = (isAccommodated) => {
    return (
      <Tag color={isAccommodated ? "success" : "error"}>
        {isAccommodated ? t("ACCOMMODATED") : t("NOT_ACCOMMODATED")}
      </Tag>
    );
  };

  // Actions function - returns array of action items for TableC
  const actions = useCallback(
    (pilgrim) => {
      const disable = pagePartiallySelected || allPageSelected;

      const isPilgrimInMutationsVariables = mutationsVariables?.pilgrims?.some(
        (p) => p.id === pilgrim.id
      );

      if (pilgrim.is_accommodated) {
        // Show cancel button for accommodated pilgrims
        return [
          {
            key: "cancel",
            label: (
              <Can anyOf={[permissions.cancel_pilgrim_accommodation]}>
              <BtnAsLink
                className="btn-danger btn-xs"
                icon={
                  isAccommodating && isPilgrimInMutationsVariables ? (
                    <Spinner size="sm" />
                  ) : (
                    <HugeiconsIcon icon={Cancel01Icon} />
                  )
                }
                onClick={() =>
                  onCancelAccommodate && onCancelAccommodate(pilgrim)
                }
                disabled={
                  (isAccommodating || loading || disable)
                }
              >
                {t("CANCEL")}
              </BtnAsLink>
              </Can>
            ),
          },
        ];
      }

      // Show accommodate button for non-accommodated pilgrims
      return [
        {
          key: "accommodate",
          label: (
            <Can anyOf={[permissions.assign_pilgrim_accommodation]}>
            <BtnAsLink
              className="btn-primary btn-xs"
              icon={
                isAccommodating && isPilgrimInMutationsVariables ? (
                  <Spinner size="sm" />
                ) : (
                  <HugeiconsIcon icon={HajiIcon} />
                )
              }
              onClick={() =>
                onAccommodate &&
                onAccommodate({ ...pilgrim, contract_id: pilgrim.contract_id })
              }
              disabled={(isAccommodating || loading || disable)}
            >
              {t("ACCOMMODATE_PILGRIMS")}
            </BtnAsLink>
            </Can>
          ),
        },
      ];
    },
    [
      onAccommodate,
      onCancelAccommodate,
      loading,
      t,
      pagePartiallySelected,
      allPageSelected,
    ]
  );
  // Define columns for TableC
  const columns = useMemo(() => {
    return [
      {
        key: "checkbox",
        name: (
          <Can anyOf={[permissions.assign_pilgrim_accommodation]}>
            <Checkbox
            className="square-checkbox"
            checked={allPageSelected}
            indeterminate={pagePartiallySelected}
            onChange={handleHeaderCheckboxChange}
            disabled={loading || (isLimitReached && !allPageSelected)}
          />
          </Can>
        ),
        render: (value, pilgrim) => {
          console.log(pilgrim.id);

          return (
            <Checkbox
              className="square-checkbox"
              checked={isPilgrimSelected(pilgrim.id)}
              onChange={(e) => handleRowCheckboxChange(pilgrim, e.target.checked)}
              disabled={!canSelectPilgrim(pilgrim)}
            />
          )
        },
      },
      {
        key: "full_name",
        name: t("PILGRIM_NAME"),
        render: (value, pilgrim) =>
          getLocalizedText(pilgrim.full_name_ar, pilgrim.full_name_en) || "--",
      },
      {
        key: "nationality_id",
        name: t("NATIONALITY"),
        render: (value) => getNationalityName(value),
      },
      {
        key: "pilgrim_uid",
        name: t("PILGRIM_UID"),
      },
      {
        key: "passport_number",
        name: t("PASSPORT_NUMBER"),
      },
      {
        key: "is_accommodated",
        name: t("ACCOMMODATION_STATUS"),
        render: (value) => renderStatus(value),
      },
      {
        key: "actions",
        name: t("ACTIONS"),
        // TableC handles actions column specially via the 'actions' prop
      },
    ];
  }, [
    allPageSelected,
    pagePartiallySelected,
    isLimitReached,
    isPilgrimSelected,
    canSelectPilgrim,
    t,
    getLocalizedText,
    onAccommodate,
    onCancelAccommodate,
    loading,
  ]);

  const options = [
    { label: t("ACCOMMODATED"), value: 1 },
    { label: t("NOT_ACCOMMODATED"), value: 0 },
    { label: t("ALL"), value: ACCOMMODATION_FILTER_ALL },
  ];

  // Handle radio group change for accommodation status filter
  const handleAccommodationStatusChange = (e) => {
    const rawValue = e.target.value;
    const value =
      rawValue === ACCOMMODATION_FILTER_ALL
        ? ACCOMMODATION_FILTER_ALL
        : Number(rawValue);
    const isAll = value === ACCOMMODATION_FILTER_ALL;
    setAccommodationStatusFilter(isAll ? ACCOMMODATION_FILTER_ALL : value);

    const updatedFilters = {
      ...filter,
      ...(isAll ? {} : { is_accommodated: value }),
    };
    if (isAll) {
      delete updatedFilters.is_accommodated;
    }

    if (onFilterChange) {
      const cleanFilters = Object.fromEntries(
        Object.entries(updatedFilters).filter(
          ([_, val]) => val !== undefined && val !== null && val !== ""
        )
      );
      onFilterChange(cleanFilters);
    }
  };

  // Handle filter apply from TableC filter modal
  const handleFilterApply = (filterData) => {
    const next = {
      ...filterData,
      ...(accommodationStatusFilter !== ACCOMMODATION_FILTER_ALL
        ? { is_accommodated: accommodationStatusFilter }
        : {}),
    };

    // Avoid sending empty multi-select values to the API
    if (Array.isArray(next.raw_group_names) && next.raw_group_names.length === 0) {
      delete next.raw_group_names;
    }

    onFilterChange(next);
  };

  // Handle filter reset
  const handleFilterReset = () => {
    onFilterChange({});
  };

  // With filters

  const groupFilterFields = [
    {
      name: "group_number",
      label: t("GROUP_NUMBER"),
      type: "input",
      disabled: true,
    },
    {
      name: "mission_id",
      label: t("ORGANIZER"),
      type: "select",
      optionKey: "missions",
      disabled: true,
      defaultValue: missionIdForGroupNames ?? null,
      options: (m) => ({
        value: m.id,
        label: <LabelValue.Value value={[m.id, getLocalizedText(m.name_ar, m.name_en)].join(" - ")} />,
      }),
    },
    {
      name: "company_id",
      label: t("AFFILIATED_ORGANIZER"),
      type: "select",
      optionKey: "companies",
      disabled: true,
      defaultValue: companyIdForGroupNames ?? null,
      options: (c) => ({
        value: c.id,
        label: <LabelValue.Value value={[c.id, getLocalizedText(c.name_ar, c.name_en)].join(" - ")} />,
      }),
    },
    {
      name: "tafweej_id",
      label: t("TAFWEEJ_ID"),
      type: "input",
    },
    {
      name: "group_name_mapping_id",
      label: t("MAPPED_GROUP_NAME"),
      type: "infiniteSelect",
      Component: (componentProps) => (
        <PilgrimGroupNameMappingInfiniteSelect
          {...componentProps}
          mission_id={missionIdForGroupNames}
          company_id={companyIdForGroupNames}
        />
      ),
      placeholder: t("MAPPED_GROUP_NAME"),
    },
    {
      name: "nusk_group_name",
      label: t("NUSUK_GROUP_NAME"),
      type: "select",
      loading: isLoadingPilgrimGroupNames,
      disabled: isLoadingPilgrimGroupNames,
      defaultValue: [],
      placeholder: t("NUSUK_GROUP_NAME"),
      options: rawGroupNamesOptions,
    },
  ];

  const campaignFilterFields = [
    {
      name: "campaign_number",
      label: t("CAMPAIGN_NUMBER"),
      type: "input",
      disabled: true,
    },
    {
      name: "nusk_group_name",
      label: t("NUSUK_GROUP_NAME"),
      type: "input",
    },
  ];

  const typeFilterFields =
    (type === "group" ? groupFilterFields : campaignFilterFields) || [];

  const filterFields = [
    ...typeFilterFields,
    {
      name: "nationality_id",
      label: t("NATIONALITY"),
      type: "select",
      optionKey: "nationalities",
      options: (n) =>
      ({
        value: String(n.id),
        label: <SelectCountry.Display value={n.id} />,
      } || []),
    },
    {
      name: "service_center_id",
      label: t("SERVICE_CENTER"),
      type: "select",
      optionKey: "service_centers",
      options: (sc) =>
      ({
        value: sc.id,
        label: sc.name,
      } || []),
    },
    {
      name: "airline_id",
      label: t("AIRLINE"),
      type: "select",
      optionKey: "airlines",
      options: (a) => ({
        value: a.id,
        label: getLocalizedText(a?.name_ar, a?.name_en) || a?.name,
      }),
    },
    {
      name: "flight_number",
      label: t("FLIGHT_NUMBER"),
      type: "input",
    },
    {
      name: "passport_no",
      label: t("PASSPORT_NUMBER"),
      type: "input",
    },
    {
      name: "date",
      label: t("DATE"),
      type: "date",
      size: "large",
      style: {
        height: "50px",
      },
    },
  ];

  return (
    <Stack gap={3}>
      <Label className="fw-bold mb-3">{t("PILGRIM_DETAILS")}</Label>
      <Radio.Group
        disabled={loading}
        block
        options={options}
        value={accommodationStatusFilter}
        onChange={handleAccommodationStatusChange}
      />
      {/* Selection Counter and Limit Warning */}
      {selectedCount > 0 && (
        <Alert
          message={t("SELECTED_COUNT")}
          type={isLimitReached ? "warning" : "info"}
          showIcon
          description={isLimitReached ? t("MAX_SELECTION_REACHED") : undefined}
        />
      )}

      {/* TableC Component */}
      <TableC
        responsive={false}
        data={data}
        columns={columns}
        loading={loading}
        filterFields={filterFields}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          onShowSizeChange: (current, size) => {
            onPageSizeChange && onPageSizeChange(size);
          },
          pageSizeOptions: [10, 20, 50, 100, 200],
        }}
        actions={actions}
        columnsFilter={false}
        filters={filter}
        onFilterApply={handleFilterApply}
        onFilterReset={handleFilterReset}
        emptyStateTitle={t("NO_PILGRIMS_FOUND")}
        emptyStateDescription={t("NO_PILGRIMS_DESCRIPTION")}
        emptyStateNoResultsTitle={t("NO_SEARCH_RESULTS")}
        emptyStateNoResultsDescription={t("NO_SEARCH_RESULTS_DESCRIPTION")}
        searchPlaceholder={t("SEARCH_HERE")}
        renderPostfixActions={
          <Flex align="center" gap={8}>
            <AntInput
              disabled={loading}
              type="text"
              name="tafweej_id"
              placeholder={t("TAFWEEJ_ID")}
              value={tafweejIdFilterValue}
              onChange={handleTafweejIdFilterChange}
              onKeyPress={(e) => {
                // Prevent non-numeric characters
                if (!/[0-9]/.test(e.key)) {
                  e.preventDefault();
                }
              }}
            />
            {type === "group" && (
              <AntInput
                disabled={loading}
                type="text"
                placeholder={t("MANIFEST_ID")}
                value={numberFilterValue}
                onChange={handleNumberFilterChange}
                onKeyPress={(e) => {
                  // Prevent non-numeric characters
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            )}
          </Flex>
        }
      />
    </Stack>
  );
};

export default AccommodationTable;
